import { useState } from 'react';
import type { ReactNode } from 'react';
import { Lightbulb, Shield, Target, ChevronDown, Info } from 'lucide-react';
import clsx from 'clsx';

/**
 * Three card primitives used throughout the wizard:
 *   - GoalCard    — what we're trying to achieve (target icon, green)
 *   - TipCard     — expandable suggestion (lightbulb icon, sky)
 *   - WarningCard — safety / important note (shield icon, amber)
 *
 * All collapsible on small screens so steps stay short — tap title to expand.
 */

interface CardProps {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
}

export function GoalCard({ title, children }: CardProps) {
  return (
    <div className="rounded-lg border border-emerald-700/40 bg-emerald-950/20 p-3 flex gap-2.5">
      <Target size={16} className="text-emerald-400 shrink-0 mt-0.5" />
      <div className="flex-1 text-sm leading-relaxed">
        <p className="font-semibold text-emerald-300 mb-0.5">{title}</p>
        <div className="text-zinc-300">{children}</div>
      </div>
    </div>
  );
}

export function TipCard({ title, children, defaultOpen = false }: CardProps) {
  return (
    <CollapsibleCard
      title={title}
      icon={<Lightbulb size={14} className="text-sky-400" />}
      defaultOpen={defaultOpen}
      colorClasses="border-sky-800/40 bg-sky-950/15"
      titleColor="text-sky-300"
    >
      {children}
    </CollapsibleCard>
  );
}

export function WarningCard({ title, children, defaultOpen = true }: CardProps) {
  return (
    <CollapsibleCard
      title={title}
      icon={<Shield size={14} className="text-amber-400" />}
      defaultOpen={defaultOpen}
      colorClasses="border-amber-700/40 bg-amber-950/20"
      titleColor="text-amber-300"
    >
      {children}
    </CollapsibleCard>
  );
}

export function InfoCard({ title, children, defaultOpen = false }: CardProps) {
  return (
    <CollapsibleCard
      title={title}
      icon={<Info size={14} className="text-zinc-400" />}
      defaultOpen={defaultOpen}
      colorClasses="border-zinc-700/60 bg-zinc-900/40"
      titleColor="text-zinc-200"
    >
      {children}
    </CollapsibleCard>
  );
}

interface CollapsibleCardProps extends CardProps {
  icon: ReactNode;
  colorClasses: string;
  titleColor: string;
}

function CollapsibleCard({
  title, children, icon, colorClasses, titleColor, defaultOpen = false,
}: CollapsibleCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={clsx('rounded-lg border', colorClasses)}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
      >
        {icon}
        <span className={clsx('flex-1 text-sm font-semibold', titleColor)}>{title}</span>
        <ChevronDown
          size={14}
          className={clsx('text-zinc-500 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="px-3 pb-3 text-sm text-zinc-300 leading-relaxed">
          {children}
        </div>
      )}
    </div>
  );
}
